// Pure rules behind the Day Close "take out / put in" dialog (F-FE-N185).
// Tested in cashMovementLogic.test.ts.
import type { ExpenseCategoryDefinition } from '@/types'
import type { MarketProblem } from '@/lib/inventoryFlow/marketLogic'

export type CashMovementType = 'paid_in' | 'paid_out'

export interface ReasonPreset {
  label: string
  /** The expense kind this reason files under. '' = not an expense. Only for
   *  money taken out; a slug the tenant has switched off is asked instead. */
  kind?: string
}

// Common reasons, one tap each. Each one taken out already knows its expense
// kind, so the cashier answers one question instead of two. Milk, ice and
// vegetables are stock (F-RPT-N22): NOOK filed milk under "Supplies" and its
// 23 Aug margin read -77.8% against +57.0% the day before.
export const REASON_PRESETS: Record<CashMovementType, ReasonPreset[]> = {
  paid_in: [
    { label: 'Owner topped up the till' },
    { label: 'Change from the bank' },
    { label: 'Staff returned an advance' },
  ],
  paid_out: [
    { label: 'Milk', kind: 'inventory_purchase' },
    { label: 'Ice', kind: 'inventory_purchase' },
    { label: 'Vegetables & groceries', kind: 'inventory_purchase' },
    { label: 'Gas', kind: 'utilities' },
    { label: 'Staff advance', kind: 'salaries' },
    { label: 'Repairs', kind: 'maintenance' },
  ],
}

// Cash handed to staff against their pay is a wage cost (F-RPT-N48). It was
// "not a cost", on the theory that the full salary is booked separately at
// month-end; across the fleet only one tenant books payroll, and it never uses
// this chip, while Secret Sky (₨18,910) and Ritual record every cost as the
// cash leaves, so their advances vanished from the ledger and the P&L. A
// tenant that does book full salaries can still file one as not a cost.
export const STAFF_ADVANCE: ReasonPreset = { label: 'Staff advance', kind: 'salaries' }

export function isStaffAdvance(reason: string): boolean {
  return reason.trim().toLowerCase().startsWith(STAFF_ADVANCE.label.toLowerCase())
}

// Staff food bought with till cash (F-RPT-N49). The fleet filed it under Other
// (Secret Sky ₨61k, Ritual and NinetyPlus ₨42k each), mostly because the
// cashier re-tapped a learned chip such as "STAFF FOOD BREAD" that had been
// filed under Other once. Such a chip now files under the staff_food kind
// when the tenant has it (migration 189 seeds it).
export const STAFF_FOOD_KIND = 'staff_food'
// Only reasons that say "staff". A bare "food bill" stays where it was filed:
// "FOOD BILL FOR CHICKEN VENDOR" is a supplier's bill, not staff food.
const STAFF_FOOD_REASON = /\bstaff\s*(food|meals?|lunch|dinner|breakfast|khana)\b/i

export function isStaffFood(reason: string): boolean {
  return STAFF_FOOD_REASON.test(reason)
}

/** A reason this restaurant has used before, with the kind it was filed under. */
export interface LearnedReason {
  movement_type: CashMovementType
  reason: string
  expense_category: string
  uses: number
}

// Cash out that is not a cost. One tap each, so "not a cost" is never a
// second question.
export const NOT_A_COST = ['Bank deposit', 'Owner took cash']

const QUICK_MAX = 6

/**
 * The one-tap reasons to offer (F-FE-N188): what this restaurant has used
 * before, and only for a restaurant with no history the stock presets. Reasons
 * whose kind is switched off, or that only repeat a chip already on screen,
 * are left out.
 */
export function quickReasons(
  type: CashMovementType,
  learned: LearnedReason[],
  categories: ExpenseCategoryDefinition[],
): { reasons: ReasonPreset[]; learned: boolean } {
  if (type === 'paid_in') {
    const mine = learned.filter((l) => l.movement_type === 'paid_in').map((l) => ({ label: l.reason }))
    return mine.length > 0
      ? { reasons: mine.slice(0, QUICK_MAX), learned: true }
      : { reasons: REASON_PRESETS.paid_in, learned: false }
  }
  if (categories.length === 0) return { reasons: REASON_PRESETS.paid_out, learned: false }
  const has = (slug: string) => categories.some((c) => c.slug === slug)
  const onScreen = new Set([
    ...NOT_A_COST.map((l) => l.toLowerCase()),
    ...categories.map((c) => kindLabel(c.slug, categories).toLowerCase()),
  ])
  const mine = learned
    .filter((l) => l.movement_type === 'paid_out')
    // Staff advance has its own chip; a learned one could still carry the old
    // "not a cost" filing.
    .filter((l) => !isStaffAdvance(l.reason))
    .filter((l) => !onScreen.has(l.reason.toLowerCase()))
    // Re-point staff food first, so the kind it is checked against below is
    // the one it will be filed under.
    .map((l) => ({
      label: l.reason,
      kind: isStaffFood(l.reason) && has(STAFF_FOOD_KIND) ? STAFF_FOOD_KIND : l.expense_category,
    }))
    .filter((r) => r.kind === '' || has(r.kind))
  if (mine.length > 0) return { reasons: mine.slice(0, QUICK_MAX), learned: true }
  return {
    reasons: REASON_PRESETS.paid_out.filter((p) => !!p.kind && has(p.kind) && p.label !== STAFF_ADVANCE.label),
    learned: false,
  }
}

/** What is saved as the reason: the chip, plus any typed detail; with only a
 *  kind picked, the detail or the kind's own name. */
export function movementReason(chip: string | null, detail: string, kindName: string): string {
  const d = detail.trim()
  if (chip) return d ? `${chip} — ${d}` : chip
  return d || kindName
}

// Counter words for the two system stock kinds; every other kind uses the
// owner's own label.
const PLAIN_KIND: Record<string, string> = {
  inventory_purchase: 'Stock purchase',
  inventory_supplier_return: 'Supplier return',
}

export function kindLabel(slug: string, categories: ExpenseCategoryDefinition[]): string {
  if (slug === '') return 'Not an expense'
  return PLAIN_KIND[slug] ?? categories.find((c) => c.slug === slug)?.label ?? slug
}

/**
 * The kind actually in force. `chosen` is null until something picks one. A
 * slug the tenant doesn't have (or switched off) counts as not chosen, so the
 * dialog asks rather than filing under a kind that isn't there.
 */
export function effectiveKind(chosen: string | null, categories: ExpenseCategoryDefinition[]): string | null {
  if (chosen === null) return null
  if (chosen === '') return ''
  return categories.some((c) => c.slug === chosen) ? chosen : null
}

/** "Stock" in the plain follow-up files under the main stock kind. */
export function stockKindFor(categories: ExpenseCategoryDefinition[]): string | null {
  const stock = categories.filter((c) => c.is_inventory)
  return (stock.find((c) => c.slug === 'inventory_purchase') ?? stock[0])?.slug ?? null
}

/** The one stock item a reason obviously means ("Milk" → "Fresh milk"), or
 *  null when there is none or more than one. */
export function stockItemForReason<T extends { name: string; is_prep?: boolean }>(items: T[], reason: string): T | null {
  const q = reason.trim().toLowerCase()
  if (!q) return null
  const buyable = items.filter((i) => !i.is_prep)
  const exact = buyable.filter((i) => i.name.trim().toLowerCase() === q)
  if (exact.length === 1) return exact[0]
  if (exact.length > 1) return null
  const words = buyable.filter((i) => i.name.toLowerCase().split(/[^a-z0-9]+/).includes(q))
  return words.length === 1 ? words[0] : null
}

const ITEM_PROBLEM: Record<MarketProblem, string> = {
  noLines: 'Add what you bought',
  badLine: 'Enter each quantity and price',
  needExpiry: 'Enter the use-by dates',
  needBuyer: 'Enter who paid',
}

/** What still stops a save, worded for the Save button; null when ready. */
export function cashMovementProblem(d: {
  reason: string
  /** True when a kind must be picked first (money out, tenant has kinds). */
  needsKind: boolean
  kind: string | null
  withItems: boolean
  itemsProblem: MarketProblem | null
  itemsTotal: number
  amount: string
}): string | null {
  if (d.needsKind && d.kind === null) return d.reason.trim() ? 'Pick what kind of cost' : 'Pick what it’s for'
  if (!d.reason.trim()) return 'Pick a reason'
  if (d.withItems) {
    if (d.itemsProblem) return ITEM_PROBLEM[d.itemsProblem]
    return d.itemsTotal > 0 ? null : 'Enter each quantity and price'
  }
  const n = Number(d.amount)
  if (d.amount.trim() === '' || !Number.isFinite(n) || n <= 0) return 'Enter an amount'
  return null
}
